import { useState, useEffect, useCallback } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import useDrivePicker from "react-google-drive-picker";
import LoadingOverlay from "../../core/LoadingOverlay";
import ConfirmDialog from "../../core/ConfirmDialog";
import DismissibleMessage from "./DismissibleMessage";
import { config } from "../../../config";
import { makeStyles } from "@mui/styles";
import { calibrationService } from "../../../services/CalibrationService";
import { listInitializer, DropdownWithAutocomplete, DatasetTypesDropdown } from "./DropdownV2";

// Raw upload dataset types that can be redriven (reprocessed) into PitGeojson + RootGeojson.
const REDRIVABLE_DATASET_TYPES = ["generic-kmz", "shapefile-zip"];

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    inputRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
    fileInfo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "20px 0",
    },
}));

export default function PITUploadTool() {
    const classes = useStyles();
    const [openPicker, data] = useDrivePicker();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [fileId, setFileId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [accessToken, setAccessToken] = useState(null);
    const [clientName, setClientName] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [clientOptions, setClientOptions] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);
    // Redrive tab state. Client/project are shared with the upload tab above the tabs; redrive only
    // needs the raw dataset type and an optional version (the whole stored source is reprocessed --
    // no group key). Versions come from get-all-dttms, which is not scoped to a group.
    const [activeTab, setActiveTab] = useState(0);
    const [redriveConfirmOpen, setRedriveConfirmOpen] = useState(false);
    const [redriveDatasetType, setRedriveDatasetType] = useState("");
    const [redriveVersion, setRedriveVersion] = useState("");
    const [redriveVersionOptions, setRedriveVersionOptions] = useState([]);
    const [redriveVersionsLoading, setRedriveVersionsLoading] = useState(false);

    const formatOptions = useCallback(
        (options) => options.data.map((option) => ({ label: option, value: option })),
        []
    );

    const initializeClients = listInitializer(
        calibrationService.getClients.bind(calibrationService),
        formatOptions,
        setClientOptions,
        () => {},
        "client list",
        setClientsLoading
    );

    const initializeProjects = listInitializer(
        calibrationService.getProjectNames.bind(calibrationService),
        formatOptions,
        setProjectOptions,
        () => {},
        "project list",
        setProjectsLoading
    );

    const initializeRedriveVersions = listInitializer(
        calibrationService.getAllDttmsFromClientProject.bind(calibrationService),
        formatOptions,
        setRedriveVersionOptions,
        () => {},
        "version list",
        setRedriveVersionsLoading
    );

    useEffect(() => {
        initializeClients();
    }, []);

    useEffect(() => {
        if (clientName) {
            initializeProjects(clientName);
        }
    }, [clientName]);

    // Load versions for the selected raw dataset type (get-all-dttms, no group). Reset the chosen
    // version whenever client/project/type changes so a stale value can't be submitted.
    useEffect(() => {
        setRedriveVersion("");
        if (clientName && projectName && redriveDatasetType) {
            initializeRedriveVersions(clientName, projectName, redriveDatasetType);
        } else {
            setRedriveVersionOptions([]);
        }
    }, [clientName, projectName, redriveDatasetType]);

    useEffect(() => {
        if (data) {
            setAccessToken(data.access_token);
        }
    }, [data]);

    const handleOpenPicker = () => {
        openPicker({
            clientId: config.googleClientId,
            developerKey: config.googleApiDeveloperKey,
            token: data?.access_token,
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: false,
            customScopes: ["https://www.googleapis.com/auth/drive.readonly"],
            callbackFunction: (pickerAction) => {
                if (pickerAction.action === "picked") {
                    setFileName(pickerAction.docs[0].name);
                    setFileId(pickerAction.docs[0].id);
                }
            },
        });
    };

    const handleSubmit = async () => {
        setConfirmModalOpen(false);
        setSubmitting(true);
        // Fire and forget -- the Lambda will exceed API Gateway's 29s timeout,
        // but the process runs regardless. Show the Progress Viewer link immediately.
        // Only surface errors that happen before the request leaves the browser (e.g. auth failure).
        calibrationService.pitGeojsonIngress(clientName, projectName, fileId, accessToken).catch((err) => {
            const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
            if (!isTimeout) {
                setErrorMessage(`Failed to submit: ${err}`);
            }
        });
        setStatusMessage("PIT GeoJSON ingress submitted. Check Progress Viewer for status.");
        setSubmitting(false);
        setFileId(null);
        setFileName(null);
    };

    const handleRedriveSubmit = async () => {
        setRedriveConfirmOpen(false);
        setSubmitting(true);
        // Fire and forget, same as upload -- redrive reruns the same long ingress process.
        // An omitted version reprocesses the latest stored raw upload for the group.
        calibrationService
            .redrivePitGeojson(clientName, projectName, redriveDatasetType, redriveVersion || null)
            .catch((err) => {
                const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
                if (!isTimeout) {
                    setErrorMessage(`Failed to submit: ${err}`);
                }
            });
        setStatusMessage("PIT GeoJSON redrive submitted. Check Progress Viewer for status.");
        setSubmitting(false);
    };

    const handleTabChange = (_event, newValue) => {
        setActiveTab(newValue);
        setStatusMessage(null);
        setErrorMessage(null);
    };

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(clientName || "")}&project=${encodeURIComponent(projectName || "")}&processTypes=PitGeojsonIngress`;

    return (
        <div style={{ paddingBottom: "200px" }}>
            <h1 style={{ textAlign: "center" }}>PIT Upload</h1>
            <Typography
                variant="body2"
                style={{ textAlign: "center", maxWidth: "640px", margin: "0 auto 20px", color: "#666" }}
            >
                Ingest PIT line geometry for a client/project. Upload a new file, or redrive an already-stored
                raw upload — both generate PitGeojson + RootGeojson and run in the background; track them in the
                Progress Viewer.
            </Typography>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm PIT GeoJSON Ingress"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={`Are you sure you want to upload for client "${clientName}" and project "${projectName}"?`}
                newLineMessage={`File: ${fileName}`}
            />
            <ConfirmDialog
                open={redriveConfirmOpen}
                title="Confirm PIT GeoJSON Redrive"
                handleClose={() => setRedriveConfirmOpen(false)}
                handleConfirm={handleRedriveSubmit}
                message={`Reprocess stored ${redriveDatasetType} upload for client "${clientName}" and project "${projectName}"?`}
                newLineMessage={`Version: ${redriveVersion || "latest"}`}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                {statusMessage && (
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                        <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">View in Progress Viewer</Link>
                    </div>
                )}
                <LoadingOverlay isLoading={submitting} message="Triggering PIT GeoJSON Ingress..." />
                <div className={classes.inputRow}>
                    <DropdownWithAutocomplete
                        label="Client"
                        value={clientName}
                        setValue={setClientName}
                        refreshOptions={initializeClients}
                        refreshDisabled={false}
                        options={clientOptions}
                        freeSolo={true}
                        isLoading={clientsLoading}
                        styleClass={{
                            flex: 1,
                            minWidth: "150px",
                            maxWidth: "300px",
                        }}
                    />
                    <DropdownWithAutocomplete
                        label="Project"
                        value={projectName}
                        setValue={setProjectName}
                        refreshOptions={() => initializeProjects(clientName)}
                        refreshDisabled={false}
                        options={projectOptions}
                        freeSolo={true}
                        isLoading={projectsLoading}
                        styleClass={{
                            flex: 1,
                            minWidth: "150px",
                            maxWidth: "300px",
                        }}
                    />
                </div>
                <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ marginBottom: "10px" }}>
                    <Tab label="Upload New File" />
                    <Tab label="Redrive Stored Upload" />
                </Tabs>
                {activeTab === 0 && (
                    <>
                        <Typography
                            variant="body2"
                            style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto 16px", color: "#666" }}
                        >
                            Upload a shapefile .zip or Google Earth .kmz/.kml from Google Drive. A
                            GeneratePitInputs container spins up (a few minutes) to generate PIT inputs. Historical
                            DLR is no longer triggered automatically — once ingress completes, use the{" "}
                            <Link to="/calibration/pitPipeline" style={{ color: "inherit" }}>Historical DLR Pipeline</Link> page to kick off the remaining phases.
                        </Typography>
                        <div className={classes.buttonRow}>
                            <Button variant="contained" onClick={handleOpenPicker}>
                                Select File From Google Drive
                            </Button>
                        </div>
                        {fileName && (
                            <div className={classes.fileInfo}>
                                <Typography variant="body1">
                                    Selected file: <strong>{fileName}</strong>
                                </Typography>
                            </div>
                        )}
                        <div className={classes.buttonRow}>
                            <Button
                                variant="contained"
                                disabled={!fileId || !clientName || !projectName || submitting}
                                onClick={() => setConfirmModalOpen(true)}
                            >
                                Submit
                            </Button>
                        </div>
                    </>
                )}
                {activeTab === 1 && (
                    <>
                        <Typography
                            variant="body2"
                            style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto 16px", color: "#666" }}
                        >
                            Reprocess a previously uploaded raw file that is already stored (no re-upload needed).
                            Pick the raw dataset type and optionally a specific version (leave version blank to use
                            the latest).
                        </Typography>
                        <div className={classes.inputRow}>
                            <DatasetTypesDropdown
                                clientName={clientName}
                                projectName={projectName}
                                value={redriveDatasetType}
                                setValue={setRedriveDatasetType}
                                setError={setErrorMessage}
                                allowedDatasetTypes={REDRIVABLE_DATASET_TYPES}
                                styleClass={{ flex: 1, mx: 1, minWidth: "150px" }}
                            />
                            <DropdownWithAutocomplete
                                label="Version (optional, latest if blank)"
                                value={redriveVersion}
                                setValue={setRedriveVersion}
                                refreshOptions={() =>
                                    initializeRedriveVersions(clientName, projectName, redriveDatasetType)
                                }
                                refreshDisabled={false}
                                options={redriveVersionOptions}
                                sortFunction={(a, b) => b.value.localeCompare(a.value)}
                                disabled={!redriveDatasetType}
                                isLoading={redriveVersionsLoading}
                                styleClass={{ flex: 1, mx: 1, minWidth: "150px" }}
                            />
                        </div>
                        <div className={classes.buttonRow}>
                            <Button
                                variant="contained"
                                disabled={!clientName || !projectName || !redriveDatasetType || submitting}
                                onClick={() => setRedriveConfirmOpen(true)}
                            >
                                Redrive
                            </Button>
                        </div>
                    </>
                )}
            </Form>
        </div>
    );
}
